import firebase from "firebase/app";
import "firebase/auth";
import "firebase/analytics";
import "firebase/remote-config";

export const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
firebase.initializeApp(config);

// Enable analytics debug mode in development
if (process.env.NODE_ENV === 'development') {
  window.localStorage.setItem('debug', 'firebase:*');
}

// Initialize Analytics with debug mode
const analytics = firebase.analytics();
analytics.setAnalyticsCollectionEnabled(true);

// Set debug mode explicitly
if (process.env.NODE_ENV === 'development') {
  analytics.setUserProperties({debug_mode: true});
  window.gtag('config', process.env.REACT_APP_FIREBASE_MEASUREMENT_ID, {
    debug_mode: true
  });
}

// Function to check if analytics is initialized and working
const checkAnalytics = () => {
  analytics.logEvent('test_event', {
    test_param: 'test_value'
  });
  console.log('Test event sent to Firebase Analytics');
};

// Run analytics check
checkAnalytics();

// Initialize Remote Config
const remoteConfig = firebase.remoteConfig();
remoteConfig.settings = {
  minimumFetchIntervalMillis: 3600000, // 1 hour
  fetchTimeoutMillis: 60000, // 1 minute
};

// Generate a consistent user ID for A/B testing
const getUserId = () => {
  let userId = localStorage.getItem('ab_test_user_id');
  if (!userId) {
    userId = Math.random().toString(36).substring(2, 15);
    localStorage.setItem('ab_test_user_id', userId);
  }
  return userId;
};

// Determine variant based on user ID
const determineVariant = (userId) => {
  // Simple hash function to consistently assign users to variants
  const hash = userId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return Math.abs(hash % 2) === 0 ? 'A' : 'B';
};

// Default values for A/B testing parameters
remoteConfig.defaultConfig = {
  landing_page_variant: determineVariant(getUserId())
};

// Function to fetch and activate remote config
export const initializeABTesting = async () => {
  try {
    // Set user ID for analytics
    analytics.setUserId(getUserId());
    await remoteConfig.fetchAndActivate();
    return true;
  } catch (error) {
    console.error('Error fetching remote config:', error);
    return false;
  }
};

// Function to get the current variant
export const getLandingPageVariant = () => {
  return remoteConfig.getString('landing_page_variant') || determineVariant(getUserId());
};

// Function to log experiment view
export const logExperimentView = (variant) => {
  try {
    const eventParams = {
      experiment_name: 'landing_page_test',
      variant: variant,
      user_id: getUserId()
    };
    console.log('Logging experiment view:', eventParams);
    
    // Log using both methods to ensure delivery
    analytics.logEvent('experiment_impression', eventParams);
    
    if (window.gtag) {
      window.gtag('event', 'experiment_impression', eventParams);
    }
    
    return true;
  } catch (error) {
    console.error('Error logging experiment view:', error);
    return false;
  }
};

// Function to log conversion events
export const logConversion = (eventName, variant) => {
  try {
    const eventParams = {
      experiment_name: 'landing_page_test',
      variant: variant,
      user_id: getUserId()
    };
    console.log('Logging conversion event:', eventName, eventParams);
    
    // Log using both methods to ensure delivery
    analytics.logEvent(eventName, eventParams);
    
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
    
    return true;
  } catch (error) {
    console.error('Error logging conversion:', error);
    return false;
  }
};

export default firebase;